/**
 * Iter 197.1 — 12-hour time formatting for the Streak system.
 *
 * The DB stores window times as 24-hour "HH:MM" strings (e.g. "22:45")
 * because they round-trip cleanly with backend timezone math. All USER-
 * facing surfaces must render 12-hour "10:45 PM" for readability — this
 * helper is the single source of truth so we don't have HH:MM leaking
 * to a corner of the UI.
 */

/**
 * Turn a "HH:MM" 24-hour string into "H:MM AM/PM".
 * Robust to missing / malformed input — returns the original string when
 * it can't parse (safe fallback for API errors).
 *
 *   fmt12h("22:45")  → "10:45 PM"
 *   fmt12h("00:15")  → "12:15 AM"
 *   fmt12h("07:00")  → "7:00 AM"
 *   fmt12h("12:00")  → "12:00 PM"
 *   fmt12h("garbage")→ "garbage"
 */
export function fmt12h(hhmm) {
  if (typeof hhmm !== "string") return hhmm ?? "";
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (isNaN(h) || h < 0 || h > 23) return hhmm;
  const ampm = h < 12 ? "AM" : "PM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

/** "22:00 – 23:00" → "10:00 PM – 11:00 PM". Uses en-dash. */
export function fmt12hRange(start, end) {
  return `${fmt12h(start)} – ${fmt12h(end)}`;
}

/**
 * Human copy for the streak-day anchor.
 *   "Next day starts at 10:00 PM"
 *
 * Passing an empty/missing start returns a friendly copy pointing the
 * student to set their window — never the misleading "12:00 AM".
 */
export function nextStreakDayCopy(startHHmm) {
  if (!startHHmm) return "Next day starts at your check-in time";
  return `Next day starts at ${fmt12h(startHHmm)}`;
}
